import React from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import getShippingMethodName from '../../../utils/getShippingMethodName';
import getShippingMethodDescription from '../../../utils/getShippingMethodDescription';
import { methods as methodsApi } from '@afosto/fulfilment/api/Methods';
import translations from './translations';

const fetchMethods = intl => async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => methodsApi.listMethods({}, requestOptions),
    {
      transformData: data =>
        data.reduce(
          (acc, method) => [
            ...acc,
            {
              ...method,
              name: getShippingMethodName(intl, method),
              description: getShippingMethodDescription(intl, method),
            },
          ],
          [],
        ),
    },
  );

const ShippingMethods = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['lcs:handling:read']);

  const intl = useIntl();
  const dataSource = useDataSource(fetchMethods(intl), {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'description',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.description',
            defaultMessage: 'Description',
            description: 'The column name for column description',
          }),
        },
        {
          key: 'max_weight',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.maxWeight',
            defaultMessage: 'Max weight',
            description: 'The column name for column max weight',
          }),
        },
        {
          key: 'delivery_during',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.deliveryDuring',
            defaultMessage: 'Delivery during',
            description: 'The column name for column delivery during',
          }),
          type: 'translation',
          formatOptions: {
            translationKey: 'deliveryDuringOptions.{value}',
          },
        },
        {
          key: 'is_same_day',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.isSameDay',
            defaultMessage: 'Same day delivery',
            description: 'The column name for column is same day',
          }),
          type: 'boolean',
        },
        {
          key: 'is_sunday',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.isSunday',
            defaultMessage: 'Sunday delivery',
            description: 'The column name for column is sunday',
          }),
          type: 'boolean',
        },
        {
          key: 'carrier_code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.carrierCode',
            defaultMessage: 'Carrier',
            description: 'The column name for column carrier code',
          }),
        },
        {
          key: 'provider',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.provider',
            defaultMessage: 'Provider',
            description: 'The column name for column provider',
          }),
        },
        {
          key: 'is_configured',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingMethods.isConfigured',
            defaultMessage: 'Configured',
            description: 'The column name for column is configured',
          }),
          type: 'boolean',
        },
      ],
    },
  });

  const renderMethod = method => (
    <Grid key={method.id} item>
      <Card>
        <Box p={2}>
          <ListItemText
            primary={getShippingMethodName(intl, method)}
            secondary={
              <>
                <Typography variant="body2">
                  {getShippingMethodDescription(intl, method)}
                </Typography>
                <Typography variant="body2">{method.provider}</Typography>
              </>
            }
          />
        </Box>
      </Card>
    </Grid>
  );

  const renderMethodSkeleton = key => (
    <Grid key={key} item>
      <Card>
        <Box p={2}>
          <SkeletonBody lines={3} />
        </Box>
      </Card>
    </Grid>
  );

  if (!hasPermissions()) {
    return <UnauthorizedComponent showActions={false} />;
  }

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={renderMethod}
          renderSkeletonItem={renderMethodSkeleton}
          component={props => <Grid container direction="column" spacing={1} {...props} />}
          emptyMessage={intl.formatMessage(translations.emptyMessage)}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid dataSource={dataSource} />
      </Hidden>
    </>
  );
};

export default ShippingMethods;
